import styled from '@emotion/styled'
import { useDebouncedCallback } from '@mantine/hooks'
import { AD_UNIT_NAME_ERROR, AD_UNIT_NAME_REGEX, CreatePbsAdUnit, PbsAdUnitBulkCreateResponse } from '@pubstack/common/src/pbs/adunit'
import { ChangeEvent, ClipboardEvent, FunctionComponent, useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useBulkCreateAdUnits } from '~/api/pbs/ad-units.api'
import { Colors } from '~/assets/style/colors'
import Button from '~/components/Button'
import { Flyout } from '~/components/Flyout'
import { IconWithTooltip } from '~/components/IconWithTooltip'
import { Input } from '~/components/Input'
import { useToast } from '~/components/Toast/useToasts'
import { parsePastedNames } from '~/utils/pastedNames'
import { isDuplicatedAdUnitName, normalizeAdUnitName, toSubmittableAdUnitNames } from './adUnitNameValidation'
import { useAdUnitNameAvailability } from './useAdUnitNameAvailability'

const AddAdUnitForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;

  & > section {
    padding-right: 4rem;
    display: inline-flex;
    gap: 1.25rem;

    & > * {
      flex-grow: 1;
    }

    & > button {
      margin-top: 0.25rem;
    }
  }
`

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const CheckingStatus = styled.span`
  margin-right: auto;
  color: ${Colors.Hurricane};
  font-size: 0.75rem;
`

const NAME_CHECK_DEBOUNCE_TIME = 300 // ms

const emptyAdUnit = (): CreatePbsAdUnit => ({ name: '', enabled: true, env: 's2s' })

const NAME_ERROR_MESSAGES: Record<string, string> = {
  required: 'Ad unit name is required',
  pattern: AD_UNIT_NAME_ERROR,
  unique: 'This name is already used.',
  duplicate: 'This name is already in the list.',
}

type Props = {
  onClose: () => void
}

export const CreateAdUnitsFlyout: FunctionComponent<Props> = ({ onClose }) => {
  // State
  const [apiErrors, setApiErrors] = useState<(PbsAdUnitBulkCreateResponse | undefined)[]>([])
  const [namesToValidate, setNamesToValidate] = useState<string[]>([])

  // API Hooks
  const { mutateAsync: bulkCreateAdUnits, isPending: isAdUnitCreationPending } = useBulkCreateAdUnits()
  // Every row this flyout creates is s2s — `AdUnitsPage` sends in-app and AMP to the edit page instead —
  // and a name is only taken within its own env, so that is the list to check against.
  const { isTaken, isChecking, takenNames } = useAdUnitNameAvailability(namesToValidate, 's2s')

  // Hooks
  const {
    control,
    reset,
    handleSubmit,
    getValues,
    trigger,
    formState: { errors, isValid, isDirty, isSubmitting },
  } = useForm<{ adUnits: CreatePbsAdUnit[] }>({
    defaultValues: { adUnits: [emptyAdUnit()] },
    mode: 'onChange',
  })
  const toast = useToast()

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'adUnits',
  })

  // Names are read on demand rather than through a reactive watch, so typing
  // re-renders only the edited field instead of the whole list.
  const scheduleNameCheck = useDebouncedCallback(() => setNamesToValidate(toSubmittableAdUnitNames(getValues('adUnits'))), NAME_CHECK_DEBOUNCE_TIME)

  // Rows added at once are validated by RHF in `onlyCheckValid` mode, which computes isValid
  // without persisting errors. Replaying the rules once they are registered is what surfaces those
  // errors — on row count too, since a cached answer arrives without `takenNames` ever changing.
  useEffect(() => {
    if (fields.length > 1) trigger('adUnits')
  }, [fields.length, takenNames, trigger])

  const onSubmit = async (values: { adUnits: CreatePbsAdUnit[] }) => {
    try {
      const request: CreatePbsAdUnit[] = values.adUnits.map((e) => ({ ...e, name: normalizeAdUnitName(e.name) })).filter((e) => e.name)

      await bulkCreateAdUnits(request)

      toast.success(
        request.length > 1 ? (
          'Ad units have been added to your inventory.'
        ) : (
          <>
            <strong>{request[0].name}</strong> has been added to your inventory.
          </>
        )
      )

      onClose()
    } catch (error) {
      setApiErrors(error)
    }
  }

  const onNameChange = (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
    // Returning the previous array untouched keeps React from re-rendering on every keystroke
    setApiErrors((previous) => (previous[index] ? previous.map((apiError, i) => (i === index ? undefined : apiError)) : previous))

    if (index === fields.length - 1 && normalizeAdUnitName(event.target.value)) {
      append(emptyAdUnit())
    }

    scheduleNameCheck()
  }

  const onPaste = (event: ClipboardEvent<HTMLInputElement>) => {
    event.stopPropagation()
    event.preventDefault()
    /* Split on the delimiters a list actually arrives with, never on a plain space. Ad unit names cannot
       contain spaces, which made `/\s+/` look correct — but it turned a pasted "ad unit 1" into three
       rows that each pass the name rules, instead of one row that visibly fails them. The paste reports
       what was pasted; `AD_UNIT_NAME_REGEX` is what rejects it. Same splitter as media creation. */
    const pastedNames = parsePastedNames(event.clipboardData.getData('Text'), normalizeAdUnitName)
    if (!pastedNames.length) {
      return
    }

    // A single replace rather than append + remove: two updates would make the revalidation below
    // run on the intermediate state. Empty rows are dropped on the way, hence no index juggling.
    const names = [...toSubmittableAdUnitNames(getValues('adUnits')), ...pastedNames]
    replace([...names.map<CreatePbsAdUnit>((name) => ({ ...emptyAdUnit(), name })), emptyAdUnit()])

    setNamesToValidate(names)
  }

  const getErrorMessage = (index: number) => {
    const errorType = errors?.adUnits?.[index]?.name?.type

    // The API error sits between the local rules: it only matters once the local ones pass
    if (errorType === 'required' || errorType === 'pattern') {
      return NAME_ERROR_MESSAGES[errorType]
    }
    return apiErrors[index]?.error ?? (errorType ? NAME_ERROR_MESSAGES[errorType] : undefined)
  }

  return (
    <Flyout.Content>
      <Flyout.Title>
        <Title>
          <p>New ad units</p>
          <IconWithTooltip title={'Copy/paste your ad units names from a sheet here. Do not include spaces.'} name="info" fill={Colors.Hurricane} width="16px" align="center" />
        </Title>
      </Flyout.Title>
      <Flyout.Body>
        <AddAdUnitForm>
          {fields.map((field, index) => (
            <section key={field.id}>
              <Input
                error={getErrorMessage(index)}
                rules={{
                  required: fields.length - 1 !== index,
                  pattern: AD_UNIT_NAME_REGEX,
                  validate: {
                    unique: (value) => typeof value !== 'string' || !isTaken(normalizeAdUnitName(value)),
                    duplicate: (value, formValues) => typeof value !== 'string' || !isDuplicatedAdUnitName(formValues.adUnits, value),
                  },
                }}
                control={control}
                name={`adUnits.${index}.name`}
                label="Ad unit name"
                onChange={onNameChange(index)}
                onPaste={onPaste}
              />
              {fields.length > 1 && (
                <Button
                  disabled={fields.length - 1 === index}
                  onClick={() => {
                    remove(index)
                    setApiErrors((prev) => prev.filter((_, i) => i !== index))
                  }}
                  variant="tertiary"
                  iconName="delete"
                  trackingLabel="common-settings-sites-creation-item-delete"
                />
              )}
            </section>
          ))}
        </AddAdUnitForm>
      </Flyout.Body>
      <Flyout.Actions>
        {isChecking && <CheckingStatus>Checking ad unit names...</CheckingStatus>}
        {isDirty && (
          <Button
            iconName="restore"
            onClick={() => {
              setApiErrors([])
              reset()
            }}
            variant="tertiary"
            trackingLabel="pbs-create-ad-units-restore"
          >
            Reset
          </Button>
        )}
        {/* Not disabled while names are being checked: the bulk creation revalidates server-side */}
        <Button
          disabled={!isValid || fields.length === 1 || isSubmitting}
          iconName={isAdUnitCreationPending ? 'pubstack' : undefined}
          iconSpin={isAdUnitCreationPending}
          onClick={handleSubmit(onSubmit)}
          trackingLabel="pbs-create-ad-units-save"
        >
          {isAdUnitCreationPending ? 'Adding ad units...' : 'Add ad units'}
        </Button>
      </Flyout.Actions>
    </Flyout.Content>
  )
}
